/**
 * Maps the calendar's current view into seeds for the embedded schedule report
 * console (opened from the calendar download button). Kept in one place so the
 * report field key + id mapping — the parts that can only be verified against
 * the live schedule report definition — are easy to adjust.
 */

import type { ReportFilterSpec } from '@features/reports/types';
import { localCalendarDate } from '@shared/dates/calendarDate';

import type { ViewMode } from '../types';

/**
 * The schedule report's provider column key, which filters on the clinician's
 * display NAME (not the calendar's `user_id`). Backend-driven: the report
 * definition (GET /v2/provider/reports) is not in this repo, so this is the
 * single knob to change if the live `schedule` report names its provider
 * column differently. A wrong key is dropped silently by the console's
 * `effectiveFilters` guard (no crash) — the report just opens unfiltered.
 */
export const SCHEDULE_REPORT_PROVIDER_FIELD = 'clinician';

const toLocalIsoDate = (date: Date): string => localCalendarDate(date);

/**
 * Convert the calendar's current view (day / week / month around `currentDate`)
 * into a report date window as local `YYYY-MM-DD` strings, matching the range
 * the calendar visually shows. Week boundaries mirror `selectCurrentDateRange`
 * (Sunday–Saturday).
 */
export const calendarViewToReportWindow = (
  viewMode: ViewMode,
  currentDate: Date,
): { start: string; end: string } => {
  if (viewMode === 'week') {
    const start = new Date(currentDate);
    start.setDate(currentDate.getDate() - currentDate.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: toLocalIsoDate(start), end: toLocalIsoDate(end) };
  }

  if (viewMode === 'month') {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    return { start: toLocalIsoDate(start), end: toLocalIsoDate(end) };
  }

  // day (and any unexpected mode): the single selected date.
  return { start: toLocalIsoDate(currentDate), end: toLocalIsoDate(currentDate) };
};

/** Inclusive day count for a `{ start, end }` window of `YYYY-MM-DD` strings. */
export const reportWindowDays = (window: { start: string; end: string }): number => {
  const start = new Date(`${window.start}T00:00:00`).getTime();
  const end = new Date(`${window.end}T00:00:00`).getTime();
  return Math.round((end - start) / 86_400_000) + 1;
};

/**
 * The clinician display name the schedule report's `clinician` column renders
 * for a person: `first_name last_name` when present, otherwise the legacy
 * display name. This mirrors the backend's report projection, so a filter value
 * built from it actually matches the report's rows.
 *
 * Why this matters: the calendar and org-members payloads carry only the legacy
 * display name, which frequently differs from `first_name last_name` — seeding
 * the legacy name matched zero rows and opened the report empty. We can only
 * build first/last for the CURRENT user (their billing info); other clinicians
 * expose no first/last on the frontend, so Practice-view provider seeding is
 * deferred to a backend change (see `CalendarNavigation`).
 */
export const reportClinicianName = (parts: {
  firstName?: string | null;
  lastName?: string | null;
  legacyName?: string | null;
}): string | null => {
  const composed = `${parts.firstName ?? ''} ${parts.lastName ?? ''}`.trim();
  return composed || parts.legacyName?.trim() || null;
};

/**
 * Build the report's provider row filter directly from clinician display NAMES
 * (already projected via `reportClinicianName`). Blank/nullish names are
 * dropped; an empty result → no filter (the report shows all).
 *
 * The explicit "calendar filtered to zero providers" case never reaches here —
 * `CalendarNavigation` disables the download button in that state, since the
 * report has no way to express an empty-selection filter and would otherwise
 * misleadingly show every provider.
 */
export const providerFilterFromNames = (
  names: (string | null | undefined)[],
): ReportFilterSpec[] => {
  const values = names
    .map(name => name?.trim())
    .filter((name): name is string => Boolean(name));
  if (values.length === 0) return [];
  return [
    {
      field: SCHEDULE_REPORT_PROVIDER_FIELD,
      op: 'any_of',
      values,
    },
  ];
};
